import React from 'react';
import { useAuth } from '../hooks/useAuth';
import { Button } from './ui/Button';
import { Link2, Plus, LogOut, Shield, UserCheck, RefreshCw } from 'lucide-react';

interface NavbarProps {
  onOpenCreateModal?: () => void;
  activePath: string;
  onNavigate: (path: string) => void;
}

export const Navbar: React.FC<NavbarProps> = ({ onOpenCreateModal, onNavigate }) => {
  const { user, devUserEmail, setDevUserEmail, logout } = useAuth();
  const isDevMode = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

  return (
    <header className="sticky top-0 z-40 w-full border-b border-slate-800/80 bg-slate-950/80 backdrop-blur-md">
      <div className="flex h-16 items-center justify-between px-4 sm:px-6 max-w-7xl mx-auto">
        {/* Brand & Title */}
        <div className="flex items-center gap-4">
          <div
            onClick={() => onNavigate('/admin')}
            className="flex items-center gap-2.5 cursor-pointer group"
          >
            <div className="w-9 h-9 rounded-xl bg-gradient-to-tr from-orange-600 to-amber-500 flex items-center justify-center shadow-lg shadow-orange-950/40 border border-orange-400/30 group-hover:scale-105 transition-transform">
              <Link2 className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-extrabold text-slate-100 tracking-tight text-base">
                  BROTOTYPE
                </span>
                <span className="text-xs px-2 py-0.5 rounded-md bg-orange-500/15 text-orange-400 font-semibold border border-orange-500/30">
                  LNK
                </span>
              </div>
              <p className="text-[10px] text-slate-400 tracking-wider uppercase font-medium">
                Marketing Link Manager
              </p>
            </div>
          </div>
        </div>

        {/* Right side actions */}
        <div className="flex items-center gap-3">
          {/* Quick Create Link CTA */}
          <Button
            size="sm"
            onClick={onOpenCreateModal || (() => onNavigate('/admin/links/new'))}
            leftIcon={<Plus className="w-4 h-4" />}
          >
            Create Link
          </Button>

          {/* Authenticated Staff User Chip */}
          {user && (
            <div className="flex items-center gap-2.5 px-3 py-1.5 rounded-lg bg-slate-900/90 border border-slate-800 text-xs">
              <span className="w-2 h-2 rounded-full bg-emerald-400 shadow-sm shadow-emerald-400/50" />
              <div className="text-left">
                <p className="font-semibold text-slate-200 truncate max-w-[140px] sm:max-w-[180px]">
                  {user.name || user.email}
                </p>
                <p className="text-[10px] text-orange-400 uppercase font-mono tracking-wider">
                  {user.role}
                </p>
              </div>
            </div>
          )}

          {/* Sign Out Button */}
          <button
            onClick={logout}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-slate-400 hover:text-rose-400 bg-slate-900/60 hover:bg-rose-500/10 border border-slate-800 hover:border-rose-500/30 transition-all cursor-pointer"
            title="Sign Out of Staff Portal"
            id="nav-sign-out-btn"
          >
            <LogOut className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Sign Out</span>
          </button>
        </div>
      </div>
    </header>
  );
};
